import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
    ArrowLeft, Phone, Envelope, EnvelopeSimple, MapPin, Flame, CheckCircle, ChatCenteredText,
    PaperPlaneRight, Stethoscope, ArrowRight, NotePencil, Sparkle, Question, Clock,
    PhoneSlash, PhoneIncoming, XCircle, Tray, UserSwitch, ClockClockwise, PencilSimple,
    ArrowUUpLeft, Lightning, Pulse, Target, WhatsappLogo, X, Prohibit,
} from "@phosphor-icons/react";

// iter98.99eu — REMOVED the standalone "Mark contacted" / "Scan
// scheduled" / "Close lead" status-flow buttons. They duplicated
// workflow that outcome chips + the Resolve panel cover, and they
// cluttered the top of the page with redundant CTAs. The single
// source of truth for status changes is now: (a) outcome chips
// auto-transition, (b) the bottom Resolve panel finalises (handoff
// or close with reason).

// iter98.99eu — Reason picker for the Resolve > Close flow. Each id
// matches the Literal in models.py LeadStatusUpdate.close_reason.
const CLOSE_REASONS = [
    { id: "not_interested",  label: "Not interested",                    icon: Prohibit },
    { id: "already_treated", label: "Already treated elsewhere",         icon: CheckCircle },
    { id: "wrong_contact",   label: "Wrong contact info",                icon: XCircle },
    { id: "budget_too_low",  label: "Budget too low",                    icon: XCircle },
    { id: "not_a_fit",       label: "Not a clinical fit",                icon: XCircle },
    { id: "no_response",     label: "No response after multiple tries", icon: PhoneSlash },
    { id: "other",           label: "Other",                             icon: PencilSimple },
];
const CLOSE_REASON_LABEL = Object.fromEntries(CLOSE_REASONS.map((r) => [r.id, r.label]));

// iter98.99eq — Lead source pills, mirroring CoeLeadsList.jsx so the
// detail page reads identically to the list. Kept inline to avoid a
// cross-page shared-module refactor while the rest of the UI stays
// frozen for the user's design pass.
const LEAD_SOURCE_PILLS = {
    "smile-quiz":          { label: "Smile Quiz",   Icon: Sparkle,        cls: "bg-violet-100 text-violet-700 border-violet-200" },
    "smile-assessment":    { label: "Assessment",   Icon: Sparkle,        cls: "bg-violet-100 text-violet-700 border-violet-200" },
    "counsellor-callback": { label: "Callback",     Icon: Phone,          cls: "bg-amber-100 text-amber-800 border-amber-200" },
    "contact-form":        { label: "Contact Form", Icon: EnvelopeSimple, cls: "bg-sky-100 text-sky-700 border-sky-200" },
};

// iter98.99es — Structured contact-outcome chips. Each chip carries a
// note prefix the composer auto-applies and an icon for the timeline
// pill. The backend mirrors the same canonical keys (LeadNote.outcome
// Literal in models.py) and auto-transitions the lead status.
const OUTCOME_CHIPS = [
    { id: "answered",   label: "Answered",        prefix: "Answered: ",       Icon: PhoneIncoming, cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    { id: "no_answer",  label: "No answer",       prefix: "No answer — ",     Icon: PhoneSlash,    cls: "bg-amber-100 text-amber-800 border-amber-200" },
    { id: "callback",   label: "Callback later",  prefix: "Callback later: ", Icon: ClockClockwise, cls: "bg-violet-100 text-violet-700 border-violet-200" },
    { id: "wrong_num",  label: "Wrong number",    prefix: "Wrong number — ",  Icon: XCircle,       cls: "bg-rose-100 text-rose-700 border-rose-200" },
];
const OUTCOME_LOOKUP = Object.fromEntries(OUTCOME_CHIPS.map((c) => [c.id, c]));

// iter98.99es — System-event types from db.lead_events get a friendly
// label + icon + tone for the unified Activity timeline. Anything not
// listed renders with a neutral pencil — better to show the row than
// hide it.
const EVENT_KIND = {
    self_claim:    { label: "Claimed from Shared Inbox",     Icon: Tray,         tone: "bg-sky-100 text-sky-700" },
    auto_release:  { label: "Auto-released back to pool",    Icon: ArrowUUpLeft, tone: "bg-amber-100 text-amber-800" },
    admin_assign:  { label: "Admin assigned to you",         Icon: UserSwitch,   tone: "bg-indigo-100 text-indigo-700" },
    status_change: { label: "Status changed",                Icon: Lightning,    tone: "bg-violet-100 text-violet-700" },
    handoff:       { label: "Referred to provider",          Icon: Stethoscope,  tone: "bg-emerald-100 text-emerald-700" },
};

function relAge(iso) {
    if (!iso) return null;
    try {
        const ms = Date.now() - new Date(iso).getTime();
        if (!Number.isFinite(ms) || ms < 0) return null;
        if (ms < 60_000) return "just now";
        const mins = Math.floor(ms / 60_000);
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(ms / 3_600_000);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(ms / 86_400_000);
        return `${days}d ago`;
    } catch { return null; }
}
function fullStamp(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            day: "numeric", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return ""; }
}

// iter98.99eu — Compute the single "next action" the PES should take
// right now, based on lead status + age. Drives the prominent banner
// at the top of the page. Returns {label, tone} where tone keys into
// the color tokens defined inline. Null means resolved / no action.
function nextActionFor(lead) {
    if (!lead) return null;
    const status = lead.lead_status;
    const hoursSinceTouch = (() => {
        const iso = lead.last_action_at || lead.status_updated_at || lead.hub_assigned_at;
        if (!iso) return null;
        try { return (Date.now() - new Date(iso).getTime()) / 3_600_000; } catch { return null; }
    })();

    if (status === "handed_off") {
        return {
            label: lead.partner_doctor_name
                ? `Referred to ${lead.partner_doctor_name}. Confirm scan once visit happens.`
                : "Referred to provider. Confirm scan once visit happens.",
            tone: "emerald",
        };
    }
    if (status === "closed") {
        const reason = CLOSE_REASON_LABEL[lead.close_reason] || "Reason not recorded";
        return { label: `Closed · ${reason}`, tone: "muted" };
    }
    if (status === "invalid") {
        return { label: "Marked invalid — wrong number / unreachable.", tone: "muted" };
    }
    if (status === "contacted") {
        if (hoursSinceTouch != null && hoursSinceTouch >= 24) {
            return { label: `Stale follow-up — last touch ${Math.floor(hoursSinceTouch)}h ago. Call again.`, tone: "rose" };
        }
        return { label: "Awaiting patient reply. Check back in a few hours.", tone: "violet" };
    }
    if (status === "scan_scheduled") {
        return { label: "Scan is scheduled. Confirm the visit took place.", tone: "cyan" };
    }
    // new / assigned / open
    if (lead.preferred_slot) {
        return { label: `Patient asked for ${lead.preferred_slot} — call them in that window.`, tone: "primary" };
    }
    return { label: "Call the patient to introduce yourself and qualify intent.", tone: "primary" };
}

const STATUS_PILLS = {
    // iter98.99en — Unified "Open" label for untouched leads (new+assigned).
    new:            { label: "Open",           cls: "bg-amber-100 text-amber-800" },
    assigned:       { label: "Open",           cls: "bg-amber-100 text-amber-800" },
    contacted:      { label: "Contacted",      cls: "bg-violet-100 text-violet-700" },
    scan_scheduled: { label: "Scan scheduled", cls: "bg-cyan-100 text-cyan-700" },
    handed_off:     { label: "Referred to provider", cls: "bg-emerald-100 text-emerald-700" },
    closed:         { label: "Closed",         cls: "bg-secondary text-muted-foreground" },
    // iter98.99eh — render modern WEB_LEAD_STATUSES too.
    consulting:     { label: "Consulting",     cls: "bg-indigo-100 text-indigo-700" },
    converted:      { label: "Converted",      cls: "bg-emerald-100 text-emerald-700" },
    not_interested: { label: "Not interested", cls: "bg-slate-100 text-slate-600" },
    invalid:        { label: "Invalid",        cls: "bg-rose-100 text-rose-700" },
};

const ANSWER_FIELDS = [
    { key: "concern", label: "Concern" },
    { key: "severity", label: "Severity" },
    { key: "age", label: "Age" },
    { key: "history_braces", label: "Prior treatment" },
    { key: "timeline", label: "Timeline" },
];

function scoreTier(score) {
    if (score >= 70) return { label: "Hot", cls: "bg-rose-100 text-rose-700 border-rose-200" };
    if (score >= 45) return { label: "Warm", cls: "bg-amber-100 text-amber-700 border-amber-200" };
    if (score > 0) return { label: "Cool", cls: "bg-sky-100 text-sky-700 border-sky-200" };
    return { label: "Cold", cls: "bg-secondary text-muted-foreground border-border" };
}

export default function CoeLeadDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [lead, setLead] = useState(null);
    const [doctors, setDoctors] = useState([]);
    const [note, setNote] = useState("");
    const [outcome, setOutcome] = useState(null);    // iter98.99es — Selected contact-outcome chip
    const [events, setEvents] = useState([]);        // iter98.99es — System events for the unified Activity log
    const [submittingNote, setSubmittingNote] = useState(false);
    const [showHandoff, setShowHandoff] = useState(false);
    const [pickedDoctorId, setPickedDoctorId] = useState("");
    const [handoffNote, setHandoffNote] = useState("");
    const [handoffBusy, setHandoffBusy] = useState(false);
    // iter98.99eu — Inline "Close lead" resolve flow (reason picker + note).
    const [showClose, setShowClose] = useState(false);
    const [closeReason, setCloseReason] = useState(null);
    const [closeNote, setCloseNote] = useState("");
    const [closeBusy, setCloseBusy] = useState(false);

    useEffect(() => {
        Promise.all([
            api.get(`/coe/leads/${id}`),
            api.get("/doctors"),
            api.get(`/coe/leads/${id}/events`).catch(() => ({ data: [] })),
        ]).then(([l, d, e]) => {
            setLead(l.data);
            setDoctors(d.data || []);
            setEvents(e.data || []);
        }).catch(() => {
            toast.error("Could not load lead");
            navigate("/coe/leads");
        });
    }, [id, navigate]);

    const refresh = async () => {
        const [{ data: l }, eRes] = await Promise.all([
            api.get(`/coe/leads/${id}`),
            api.get(`/coe/leads/${id}/events`).catch(() => ({ data: [] })),
        ]);
        setLead(l);
        setEvents(eRes.data || []);
    };

    const addNote = async () => {
        const text = note.trim();
        if (!text) return;
        setSubmittingNote(true);
        try {
            await api.post(`/coe/leads/${id}/note`, { text, outcome });
            setNote("");
            setOutcome(null);
            await refresh();
            toast.success(outcome ? `Logged · ${OUTCOME_LOOKUP[outcome].label}` : "Note added");
        } catch {
            toast.error("Failed to add note");
        } finally {
            setSubmittingNote(false);
        }
    };

    // iter98.99es — Tap a chip → auto-prefix the note + remember the
    // outcome. Tap the same chip again to clear (and strip the prefix
    // if it's still leading the textarea). Lets PES log a no-answer
    // attempt without typing anything ("No answer — " is enough).
    const pickOutcome = (id) => {
        if (outcome === id) {
            // Clear: strip the current prefix if it's at the start.
            const currentPrefix = OUTCOME_LOOKUP[id].prefix;
            setNote((n) => (n.startsWith(currentPrefix) ? n.slice(currentPrefix.length) : n));
            setOutcome(null);
            return;
        }
        const next = OUTCOME_LOOKUP[id];
        // Strip any other outcome's prefix that may be at the start.
        let stripped = note;
        for (const c of OUTCOME_CHIPS) {
            if (stripped.startsWith(c.prefix)) {
                stripped = stripped.slice(c.prefix.length);
                break;
            }
        }
        setNote(next.prefix + stripped);
        setOutcome(id);
    };

    // iter98.99eu — Resolve > Close with a categorical reason. The /status
    // endpoint enforces close_reason when status="closed" (validated by
    // the Pydantic Literal in LeadStatusUpdate).
    const submitClose = async () => {
        if (!closeReason) {
            toast.error("Pick a reason before closing the lead.");
            return;
        }
        setCloseBusy(true);
        try {
            await api.put(`/coe/leads/${id}/status`, {
                status: "closed",
                close_reason: closeReason,
                note: closeNote.trim() || "",
            });
            await refresh();
            setShowClose(false);
            setCloseReason(null);
            setCloseNote("");
            toast.success(`Lead closed · ${CLOSE_REASON_LABEL[closeReason]}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't close the lead");
        } finally {
            setCloseBusy(false);
        }
    };

    const submitHandoff = async () => {
        if (!pickedDoctorId) { toast.error("Pick a partner doctor"); return; }
        setHandoffBusy(true);
        try {
            await api.post(`/coe/leads/${id}/handoff`, { doctor_id: pickedDoctorId, note: handoffNote });
            await refresh();
            setShowHandoff(false);
            setPickedDoctorId("");
            setHandoffNote("");
            toast.success("Lead referred to provider");
        } catch {
            toast.error("Handoff failed");
        } finally {
            setHandoffBusy(false);
        }
    };

    if (!lead) return <div className="text-muted-foreground">Loading…</div>;

    const tier = scoreTier(lead.score || 0);
    const status = STATUS_PILLS[lead.lead_status] || STATUS_PILLS.assigned;
    const notes = [...(lead.notes_log || [])].reverse();
    const isHandedOff = lead.lead_status === "handed_off";
    const isClosed = lead.lead_status === "closed" || lead.lead_status === "invalid";
    const isResolved = isHandedOff || isClosed;
    const nextAction = nextActionFor(lead);

    return (
        <div data-testid="coe-lead-detail">
            <Link to="/coe/leads" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-6">
                <ArrowLeft size={14} /> Back to My Queue
            </Link>

            {/* Header */}
            <div className="bg-card border border-border rounded-2xl p-6 mb-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="font-display text-2xl font-bold">{lead.name}</h1>
                            {/* iter98.99eo — Human-readable Lead ID in the header. */}
                            {lead.code && (
                                <span
                                    className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-secondary/60 text-muted-foreground border border-border"
                                    data-testid="coe-lead-detail-code"
                                >
                                    {lead.code}
                                </span>
                            )}
                            <span className={`text-[10px] px-2 py-1 rounded-full border uppercase font-semibold inline-flex items-center gap-1 ${tier.cls}`}>
                                {tier.label === "Hot" && <Flame size={10} weight="fill" />}
                                {tier.label} · {lead.score || 0}
                            </span>
                            <span className={`text-[10px] px-2 py-1 rounded-full uppercase font-semibold ${status.cls}`}>
                                {status.label}
                            </span>
                            {/* iter98.99eq — Lead-source chip so the detail page
                                reads identically to the My Queue list. Mirrors
                                LEAD_SOURCE_PILLS / fallback to a neutral
                                "unknown" chip for legacy rows without source. */}
                            {(() => {
                                const src = LEAD_SOURCE_PILLS[lead.source] || {
                                    label: lead.source || "Unknown",
                                    Icon: Question,
                                    cls: "bg-secondary text-muted-foreground border-border",
                                };
                                const SrcIcon = src.Icon;
                                return (
                                    <span
                                        className={`text-[10px] px-2 py-1 rounded-full border uppercase font-semibold inline-flex items-center gap-1 ${src.cls}`}
                                        data-testid="coe-lead-detail-source"
                                    >
                                        <SrcIcon size={11} weight="fill" />
                                        {src.label}
                                    </span>
                                );
                            })()}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
                            <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 hover:text-primary"><Phone size={13} /> {lead.phone}</a>
                            {lead.email && <span className="flex items-center gap-1.5"><Envelope size={13} /> {lead.email}</span>}
                            <span className="flex items-center gap-1.5"><MapPin size={13} /> {lead.city || ","}</span>
                            {/* iter98.99es — Submission timestamp + relative age.
                                Cheap signal that PES uses on every call: is this
                                a fresh lead (act now) or stale (apologise + own it). */}
                            {lead.created_at && (
                                <span
                                    className="flex items-center gap-1.5"
                                    title={`Submitted ${fullStamp(lead.created_at)}`}
                                    data-testid="coe-lead-detail-submitted"
                                >
                                    <ClockClockwise size={13} />
                                    <span>
                                        Submitted <span className="font-medium text-foreground">{relAge(lead.created_at) || "—"}</span>
                                        <span className="text-muted-foreground/80"> · {fullStamp(lead.created_at)}</span>
                                    </span>
                                </span>
                            )}
                        </div>
                        {/* iter98.99er — Preferred call time the caller picked
                            during the Smile-Quiz post-submit step.
                            iter98.99ew — Moved INTO the quiz-answers grid
                            below (alongside Concern / Severity / Age etc.)
                            so it reads as one consistent strip rather than
                            a separate amber callout. Falls back silently
                            for legacy leads without a slot. */}
                    </div>
                </div>

                {/* Quiz answers + preferred slot (iter98.99ew — one grid). */}
                {((lead.answers && Object.keys(lead.answers).length > 0) || lead.preferred_slot) && (
                    <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                        {lead.preferred_slot && (
                            <div
                                key="__preferred_slot"
                                className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                                data-testid="coe-lead-detail-preferred-slot"
                            >
                                <div className="text-amber-700 text-[10px] uppercase tracking-wider font-bold">
                                    Caller asked for
                                </div>
                                <div className="font-medium mt-0.5 text-amber-900">{lead.preferred_slot}</div>
                            </div>
                        )}
                        {ANSWER_FIELDS.filter((f) => lead.answers && lead.answers[f.key]).map((f) => (
                            <div key={f.key} className="bg-secondary/40 rounded-lg px-3 py-2">
                                <div className="text-muted-foreground text-[10px] uppercase tracking-wider">{f.label}</div>
                                <div className="font-medium capitalize mt-0.5">{String(lead.answers[f.key]).replace(/_/g, " ")}</div>
                            </div>
                        ))}
                    </div>
                )}

                {/* iter98.99eu — REMOVED the old status-flow buttons +
                    handoff-toggle button. The page now has a single
                    clear sequence: 1) READ the header → 2) CALL & LOG
                    in the Activity panel → 3) RESOLVE via the panel at
                    the bottom. Outcome chips auto-transition status;
                    handoff/close live in the Resolve panel below. */}
                {!isHandedOff && nextAction && (
                    <div
                        className={`mt-5 pt-5 border-t border-border flex items-start gap-3 ${
                            nextAction.tone === "rose"    ? "" :
                            nextAction.tone === "violet"  ? "" :
                            nextAction.tone === "cyan"    ? "" :
                            nextAction.tone === "emerald" ? "" :
                            nextAction.tone === "muted"   ? "" : ""
                        }`}
                        data-testid="coe-lead-next-action"
                    >
                        <div className={`w-10 h-10 rounded-2xl grid place-items-center shrink-0 ${
                            nextAction.tone === "rose"    ? "bg-rose-100 text-rose-700" :
                            nextAction.tone === "violet"  ? "bg-violet-100 text-violet-700" :
                            nextAction.tone === "cyan"    ? "bg-cyan-100 text-cyan-700" :
                            nextAction.tone === "emerald" ? "bg-emerald-100 text-emerald-700" :
                            nextAction.tone === "muted"   ? "bg-secondary text-muted-foreground" :
                                                            "bg-primary/10 text-primary"
                        }`}>
                            <Target size={18} weight="duotone" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Next action</div>
                            <div className="text-[14px] font-semibold leading-tight mt-0.5">{nextAction.label}</div>
                        </div>
                    </div>
                )}

                {/* iter98.99eu — Quick outreach actions. Sit just below
                    Next Action so the PES knows what to do AND has the
                    tools to do it in one glance. WhatsApp deep-links the
                    patient's phone via wa.me; Email opens a mailto. */}
                {!isHandedOff && (
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="coe-quick-actions">
                        <a
                            href={`tel:${lead.phone}`}
                            className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-semibold shadow-sm"
                            data-testid="coe-quick-call"
                        >
                            <Phone size={15} weight="fill" /> Call now
                        </a>
                        <a
                            href={`https://wa.me/${(lead.phone || "").replace(/[^0-9]/g, "")}`}
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white transition-colors text-sm font-semibold shadow-sm"
                            data-testid="coe-quick-whatsapp"
                        >
                            <WhatsappLogo size={15} weight="fill" /> WhatsApp
                        </a>
                        {lead.email && (
                            <a
                                href={`mailto:${lead.email}`}
                                className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-secondary hover:bg-secondary/80 transition-colors text-sm font-semibold"
                                data-testid="coe-quick-email"
                            >
                                <Envelope size={15} weight="duotone" /> Email
                            </a>
                        )}
                    </div>
                )}

                {isHandedOff && lead.partner_doctor_name && (
                    <div className="mt-5 pt-5 border-t border-border flex items-center gap-2 text-sm" data-testid="coe-handoff-info">
                        <CheckCircle size={18} weight="fill" className="text-emerald-600" />
                        <span className="text-muted-foreground">Referred to</span>
                        <span className="font-medium">{lead.partner_doctor_name}</span>
                        {lead.partner_clinic_name && <span className="text-xs text-muted-foreground">({lead.partner_clinic_name})</span>}
                    </div>
                )}
            </div>

            {/* iter98.99es / iter98.99eu — Step 2 · Call & log + Activity timeline.
                Notes (PES-typed) + system events (claim / release /
                status-change / handoff) merged into a single
                reverse-chronological feed. */}
            <div className="bg-card border border-border rounded-2xl p-6" data-testid="coe-notes-panel">
                <div className="flex items-center gap-2 mb-4">
                    <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-secondary/60">
                        Step 2
                    </span>
                    <Pulse size={20} weight="duotone" className="text-primary" />
                    <h3 className="font-display text-lg font-semibold">Call &amp; log</h3>
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground ml-1">
                        Notes &amp; activity timeline
                    </span>
                </div>

                {/* Outcome-chip strip — tap one to auto-prefix the note + stamp
                    a structured outcome on the saved record. */}
                <div className="mb-2 flex flex-wrap items-center gap-2" data-testid="coe-outcome-chips">
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground pr-1">Log a call outcome</span>
                    {OUTCOME_CHIPS.map((c) => {
                        const active = outcome === c.id;
                        return (
                            <button
                                key={c.id}
                                type="button"
                                onClick={() => pickOutcome(c.id)}
                                className={`inline-flex items-center gap-1 px-2.5 h-7 text-[11.5px] font-semibold rounded-full border transition-all ${
                                    active ? `${c.cls} shadow-sm ring-2 ring-offset-1 ring-current/20` : "bg-secondary/40 border-border text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                                }`}
                                data-testid={`coe-outcome-${c.id}`}
                                aria-pressed={active}
                            >
                                <c.Icon size={11} weight="fill" />
                                {c.label}
                            </button>
                        );
                    })}
                </div>

                <div className="space-y-2 mb-4">
                    <Textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Add a quick note from your last call (e.g. 'Patient wants to start next month, budget ~₹1.5L')"
                        rows={3}
                        data-testid="coe-note-textarea"
                    />
                    <div className="flex justify-end">
                        <Button
                            size="sm"
                            disabled={submittingNote || !note.trim()}
                            onClick={addNote}
                            data-testid="coe-note-save"
                        >
                            <PaperPlaneRight size={14} className="mr-1.5" />
                            {submittingNote ? "Saving…" : "Add note"}
                        </Button>
                    </div>
                </div>

                <Timeline notes={notes} events={events} />
            </div>

            {/* iter98.99eu — Step 3 · Resolve. Final destination for every
                lead: either refer to a partner provider for an in-clinic
                scan, or close it with a categorical reason so analytics
                can attribute churn. When the lead is already resolved we
                render a summary card instead of the actions. */}
            {!isResolved ? (
                <div className="bg-card border border-border rounded-2xl p-6 mt-6" data-testid="coe-resolve-panel">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-secondary/60">
                            Step 3
                        </span>
                        <CheckCircle size={20} weight="duotone" className="text-primary" />
                        <h3 className="font-display text-lg font-semibold">Resolve this lead</h3>
                    </div>
                    <p className="text-xs text-muted-foreground mb-4">
                        Every lead must end either with a referral to a partner provider or a categorised close.
                    </p>

                    {!showHandoff && !showClose && (
                        <div className="flex flex-wrap gap-2" data-testid="coe-resolve-actions">
                            <Button
                                onClick={() => setShowHandoff(true)}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid="coe-resolve-handoff-btn"
                            >
                                <Stethoscope size={15} weight="fill" className="mr-1.5" />
                                Refer to Provider
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => setShowClose(true)}
                                className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                                data-testid="coe-resolve-close-btn"
                            >
                                <X size={15} weight="bold" className="mr-1.5" />
                                Close this lead
                            </Button>
                        </div>
                    )}

                    {/* Handoff form */}
                    {showHandoff && (
                        <div className="space-y-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4" data-testid="coe-handoff-form">
                            <div className="flex items-center justify-between gap-2">
                                <div className="text-sm font-semibold text-emerald-900 inline-flex items-center gap-1.5">
                                    <Stethoscope size={15} weight="fill" /> Refer to Provider
                                </div>
                                <button
                                    type="button"
                                    onClick={() => { setShowHandoff(false); setPickedDoctorId(""); setHandoffNote(""); }}
                                    className="text-xs text-muted-foreground hover:text-foreground"
                                    data-testid="coe-handoff-cancel"
                                >
                                    Cancel
                                </button>
                            </div>
                            <div>
                                <Label className="text-xs">Pick provider</Label>
                                <select
                                    value={pickedDoctorId}
                                    onChange={(e) => setPickedDoctorId(e.target.value)}
                                    className="mt-1 w-full h-10 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                                    data-testid="coe-handoff-doctor-select"
                                >
                                    <option value="">— Select provider —</option>
                                    {doctors.map((d) => (
                                        <option key={d.id} value={d.id}>
                                            {d.name || d.doctor_name || d.email} {d.clinic_name ? `· ${d.clinic_name}` : ""}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <Label className="text-xs">Note to provider (optional)</Label>
                                <Textarea
                                    value={handoffNote}
                                    onChange={(e) => setHandoffNote(e.target.value)}
                                    placeholder="Any context the provider should know before the scan visit."
                                    rows={2}
                                    className="mt-1"
                                    data-testid="coe-handoff-note"
                                />
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button
                                    onClick={submitHandoff}
                                    disabled={handoffBusy || !pickedDoctorId}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                    data-testid="coe-handoff-submit"
                                >
                                    {handoffBusy ? "Referring…" : <><ArrowRight size={14} className="mr-1.5" /> Confirm referral</>}
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Close form */}
                    {showClose && (
                        <div className="space-y-3 rounded-xl border border-rose-200 bg-rose-50/30 p-4" data-testid="coe-close-form">
                            <div className="flex items-center justify-between gap-2">
                                <div className="text-sm font-semibold text-rose-900 inline-flex items-center gap-1.5">
                                    <X size={15} weight="bold" /> Close this lead
                                </div>
                                <button
                                    type="button"
                                    onClick={() => { setShowClose(false); setCloseReason(null); setCloseNote(""); }}
                                    className="text-xs text-muted-foreground hover:text-foreground"
                                    data-testid="coe-close-cancel"
                                >
                                    Cancel
                                </button>
                            </div>
                            <div>
                                <Label className="text-xs">Pick a reason</Label>
                                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="coe-close-reasons">
                                    {CLOSE_REASONS.map((r) => {
                                        const active = closeReason === r.id;
                                        return (
                                            <button
                                                key={r.id}
                                                type="button"
                                                onClick={() => setCloseReason(r.id)}
                                                className={`inline-flex items-center gap-2 px-3 h-10 text-[12.5px] font-medium rounded-lg border text-left transition-all ${
                                                    active
                                                        ? "border-rose-400 bg-rose-100 text-rose-800 shadow-sm"
                                                        : "border-border bg-background text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                                                }`}
                                                data-testid={`coe-close-reason-${r.id}`}
                                                aria-pressed={active}
                                            >
                                                <r.icon size={13} weight="bold" />
                                                {r.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs">Note (optional)</Label>
                                <Textarea
                                    value={closeNote}
                                    onChange={(e) => setCloseNote(e.target.value)}
                                    placeholder="Optional context — pasted into the activity log."
                                    rows={2}
                                    className="mt-1"
                                    data-testid="coe-close-note"
                                />
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button
                                    onClick={submitClose}
                                    disabled={closeBusy || !closeReason}
                                    variant="destructive"
                                    data-testid="coe-close-submit"
                                >
                                    {closeBusy ? "Closing…" : <><X size={14} className="mr-1.5" weight="bold" /> Confirm close</>}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="bg-card border border-border rounded-2xl p-6 mt-6" data-testid="coe-resolved-summary">
                    <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-2xl grid place-items-center shrink-0 ${
                            isHandedOff ? "bg-emerald-100 text-emerald-700" : "bg-secondary text-muted-foreground"
                        }`}>
                            {isHandedOff ? <Stethoscope size={18} weight="fill" /> : <CheckCircle size={18} weight="fill" />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                                Lead resolved
                            </div>
                            <div className="text-sm font-semibold mt-0.5">
                                {isHandedOff
                                    ? (lead.partner_doctor_name
                                        ? `Referred to ${lead.partner_doctor_name}${lead.partner_clinic_name ? ` · ${lead.partner_clinic_name}` : ""}`
                                        : "Referred to provider")
                                    : `Closed${lead.close_reason ? ` · ${CLOSE_REASON_LABEL[lead.close_reason] || lead.close_reason}` : ""}`}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                                {fullStamp(lead.status_updated_at || lead.closed_at)}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// iter98.99es — Unified timeline: merges PES notes (notes_log) with
// system events (db.lead_events) into a single reverse-chrono feed.
// Notes render with the author's avatar + text + outcome chip; events
// render with a tinted icon disc + friendly label + comment fallback.
function Timeline({ notes, events }) {
    const items = useMemo(() => {
        const merged = [
            ...notes.map((n) => ({ kind: "note",  ts: n.created_at, payload: n, key: `n:${n.id}` })),
            ...events.map((e) => ({ kind: "event", ts: e.created_at, payload: e, key: `e:${e.id || `${e.event_type}-${e.created_at}`}` })),
        ];
        merged.sort((a, b) => {
            const ta = a.ts ? new Date(a.ts).getTime() : 0;
            const tb = b.ts ? new Date(b.ts).getTime() : 0;
            return tb - ta;
        });
        return merged;
    }, [notes, events]);

    if (items.length === 0) {
        return (
            <div className="text-center py-6 text-muted-foreground text-sm border-t border-border pt-4">
                <NotePencil size={28} className="mx-auto mb-2 opacity-40" />
                Nothing logged yet. Log your first call outcome above.
            </div>
        );
    }
    return (
        <div className="space-y-3 border-t border-border pt-4" data-testid="coe-activity-timeline">
            {items.map((it) => it.kind === "note"
                ? <TimelineNote n={it.payload} key={it.key} />
                : <TimelineEvent e={it.payload} key={it.key} />)}
        </div>
    );
}

function TimelineNote({ n }) {
    const oc = n.outcome ? OUTCOME_LOOKUP[n.outcome] : null;
    return (
        <div className="flex gap-3" data-testid={`coe-note-${n.id}`}>
            <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0">
                {(n.author_name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-foreground">{n.author_name}</span>
                    <span aria-hidden>·</span>
                    <span>{fullStamp(n.created_at)}</span>
                    {oc && (
                        <span
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-wider border ${oc.cls}`}
                            data-testid={`coe-note-outcome-${n.id}`}
                        >
                            <oc.Icon size={9} weight="fill" />
                            {oc.label}
                        </span>
                    )}
                </div>
                <div className="text-sm mt-1 whitespace-pre-wrap">{n.text}</div>
            </div>
        </div>
    );
}

function TimelineEvent({ e }) {
    const kind = EVENT_KIND[e.event_type] || { label: e.event_type, Icon: PencilSimple, tone: "bg-secondary text-muted-foreground" };
    return (
        <div className="flex gap-3" data-testid={`coe-event-${e.event_type}-${e.id || e.created_at}`}>
            <div className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${kind.tone}`}>
                <kind.Icon size={14} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-foreground">{kind.label}</span>
                    <span aria-hidden>·</span>
                    <span>{fullStamp(e.created_at)}</span>
                    {e.actor_name && e.actor_name !== "Auto-release sweep" && (
                        <>
                            <span aria-hidden>·</span>
                            <span>by {e.actor_name}</span>
                        </>
                    )}
                </div>
                {e.comment && (
                    <div className="text-sm mt-1 text-muted-foreground italic">{e.comment}</div>
                )}
            </div>
        </div>
    );
}
